import type { ObservationAgreementSignatureSummary } from '../../api/types';

export type SortKey = 'signerName' | 'email' | 'location' | 'signedAtUtc';

export interface SignatureTableProps {
  signatures: ObservationAgreementSignatureSummary[];
  emptyMessage: string;

  sortKey: SortKey;
  sortDescending: boolean;
  onSort: (key: SortKey) => void;

  onOpen: (signature: ObservationAgreementSignatureSummary) => void;
}

/**
 * The dashboard's table.
 *
 * Reuses the `care-table` styles the complimentary care dashboard introduced, as the CT assessment
 * table does — they are review-dashboard styles that happen to have been written for the first
 * review dashboard, and a third copy under another name would drift the moment any of them is
 * touched.
 */
export function SignatureTable({
  signatures,
  emptyMessage,
  sortKey,
  sortDescending,
  onSort,
  onOpen,
}: SignatureTableProps) {
  return (
    <div className="care-table__scroll">
      <table className="care-table care-table--fixed">
        <colgroup>
          <col className="care-col--employee" />
          <col className="care-col--enrollees" />
          <col className="care-col--enrollees" />
          <col className="care-col--date" />
        </colgroup>
        <thead>
          <tr>
            <SortableHeader label="Signed by" columnKey="signerName" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <SortableHeader label="Email" columnKey="email" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <SortableHeader label="Clinic" columnKey="location" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <SortableHeader label="Signed" columnKey="signedAtUtc" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
          </tr>
        </thead>
        <tbody>
          {signatures.map((signature) => (
            <SignatureRow key={signature.id} signature={signature} onOpen={onOpen} />
          ))}
        </tbody>
      </table>

      {signatures.length === 0 && <p className="care-empty">{emptyMessage}</p>}
    </div>
  );
}

function SignatureRow({
  signature,
  onOpen,
}: {
  signature: ObservationAgreementSignatureSummary;
  onOpen: (signature: ObservationAgreementSignatureSummary) => void;
}) {
  const signed = new Date(signature.signedAtUtc);

  return (
    <tr
      className="care-table__row"
      onClick={() => onOpen(signature)}
      tabIndex={0}
      role="button"
      aria-label={`Open the agreement ${signature.signerName} signed`}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(signature);
        }
      }}
    >
      <td>
        <div className="care-table__primary">{signature.signerName}</div>

        {/* Only the older ones say anything — a tag on every current row would be noise on what is
            the normal case. Amber rather than red: an agreement signed before the text was revised
            is exactly what the snapshot is for, not an error. */}
        {!signature.isCurrentVersion && (
          <span className="care-tag">Earlier version (v{signature.agreementVersion})</span>
        )}
      </td>
      {/* Both optional on the form, so both can be absent. Shown as a muted dash rather than left
          blank: an empty cell reads as a rendering fault, where a dash reads as an answer. */}
      <td>
        <Optional value={signature.email} />
      </td>
      <td>
        <Optional value={signature.location} />
      </td>
      <td>
        <div className="care-table__primary">{signed.toLocaleDateString()}</div>
        {/* The time is part of the record, not decoration: several people can sign on the same
            morning, and the clock is what orders and distinguishes them. */}
        <div className="care-table__muted">
          {signed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </div>
      </td>
    </tr>
  );
}

/** A value the signer may not have given. */
function Optional({ value }: { value: string | null }) {
  if (value === null) {
    return (
      <div className="care-table__muted" aria-label="Not given">
        —
      </div>
    );
  }

  return (
    <div className="care-table__primary care-table__ellipsis" title={value}>
      {value}
    </div>
  );
}

interface SortableHeaderProps {
  label: string;
  columnKey: SortKey;
  activeKey: SortKey;
  descending: boolean;
  onSort: (key: SortKey) => void;
}

function SortableHeader({ label, columnKey, activeKey, descending, onSort }: SortableHeaderProps) {
  const isActive = activeKey === columnKey;

  return (
    <th aria-sort={isActive ? (descending ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="care-table__sort" onClick={() => onSort(columnKey)}>
        {label}
        <span className="care-table__sort-arrow" aria-hidden="true">
          {isActive ? (descending ? '▾' : '▴') : ''}
        </span>
      </button>
    </th>
  );
}
